import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  Snackbar,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Warning as WarningIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import systemAPI from "../../utils/systemAPI";
import { useAuth } from "../../context/AuthContext";

/**
 * Department Management Component
 *
 * Allows the Developer Master to create, edit, and delete departments.
 * Departments are a fundamental organizational structure in the application.
 *
 * This component is only accessible to users with the Developer Master role.
 */
const DepartmentManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [accessDenied, setAccessDenied] = useState(false);

  // Check if user has developer-master role
  useEffect(() => {
    if (user && user.role !== "developer-master") {
      setAccessDenied(true);
      // Redirect non-developer users after a short delay to show access denied message
      const redirectTimer = setTimeout(() => {
        navigate("/dashboard");
      }, 3000);

      return () => clearTimeout(redirectTimer);
    }
  }, [user, navigate]);

  // State for departments
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // State for snackbar alerts
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // State for department dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("add"); // 'add' or 'edit'
  const [currentDepartment, setCurrentDepartment] = useState({
    name: "",
    code: "",
    description: "",
    location: "",
    managerEmail: "",
  });

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);

  // State for department settings dialog
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [departmentSettings, setDepartmentSettings] = useState(null);
  const [settingsTabValue, setSettingsTabValue] = useState(0);

  // State for inventory categories and units
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [defaultMinThreshold, setDefaultMinThreshold] = useState(5);

  // State for category/unit dialog
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [itemDialogMode, setItemDialogMode] = useState("category"); // 'category' or 'unit'
  const [itemDialogAction, setItemDialogAction] = useState("add"); // 'add' or 'edit'
  const [currentItem, setCurrentItem] = useState("");
  const [itemToEdit, setItemToEdit] = useState("");

  // Fetch departments from API
  const fetchDepartments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await systemAPI.getDepartments();
      setDepartments(response.data || []);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching departments:", err);
      setError("Failed to load departments. Please try again.");
      setLoading(false);

      // Set demo data for development in case of error
      setDepartments([
        {
          id: "1",
          name: "Computer Science",
          code: "CS",
          description: "Computer Science Department",
          location: "Building A, Floor 2",
          adminCount: 2,
          facultyCount: 8,
          createdAt: new Date().toISOString(),
        },
        {
          id: "2",
          name: "Biology",
          code: "BIO",
          description: "Biology Department",
          location: "Building B, Floor 1",
          adminCount: 1,
          facultyCount: 6,
          createdAt: new Date().toISOString(),
        },
        {
          id: "3",
          name: "Chemistry",
          code: "CHEM",
          description: "Chemistry Department",
          location: "Building C, Floor 3",
          adminCount: 3,
          facultyCount: 12,
          createdAt: new Date().toISOString(),
        },
        {
          id: "4",
          name: "Physics",
          code: "PHY",
          description: "Physics Department",
          location: "Building D, Floor 1",
          adminCount: 2,
          facultyCount: 7,
          createdAt: new Date().toISOString(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch department-specific settings
  const fetchDepartmentSettings = async (departmentId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await systemAPI.getDepartmentSettings(departmentId);
      setDepartmentSettings(response.data || null);

      // Initialize inventory categories and units from department settings
      const inventorySettings = response.data?.inventory || {};
      setCategories(inventorySettings.categories || []);
      setUnits(inventorySettings.units || []);
      setDefaultMinThreshold(inventorySettings.defaultMinThreshold || 5);
    } catch (err) {
      console.error(
        `Error fetching settings for department ${departmentId}:`,
        err
      );
      setError("Failed to load department settings. Using default values.");

      // Set default values in case of error
      setCategories([
        "consumable",
        "instrument",
        "glassware",
        "chemical",
        "basic_essential",
      ]);
      setUnits(["pcs", "boxes", "bottles", "liters", "grams"]);
      setDefaultMinThreshold(5);
    } finally {
      setLoading(false);
    }
  };

  // Load departments on component mount
  useEffect(() => {
    fetchDepartments();
  }, []);

  // Handle dialog opening for adding or editing a department
  const handleOpenDialog = (mode, department = null) => {
    setDialogMode(mode);
    if (mode === "edit" && department) {
      setCurrentDepartment({ ...department });
    } else {
      setCurrentDepartment({
        name: "",
        code: "",
        description: "",
        location: "",
        managerEmail: "",
      });
    }
    setDialogOpen(true);
  };

  // Handle department form changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentDepartment({
      ...currentDepartment,
      [name]: value,
    });
  };

  // Handle department submission (create or update)
  const handleSubmit = async () => {
    try {
      if (dialogMode === "add") {
        // Create new department
        const response = await systemAPI.createDepartment({
          name: currentDepartment.name,
          code: currentDepartment.code,
          description: currentDepartment.description,
          location: currentDepartment.location,
          managerEmail: currentDepartment.managerEmail,
        });

        // Add the new department to the list
        setDepartments([...departments, response.data]);

        // Show success message
        setSnackbar({
          open: true,
          message: "Department created successfully",
          severity: "success",
        });
      } else {
        // Update existing department
        console.log("Updating department with ID:", currentDepartment.id);
        console.log("Department data being sent:", currentDepartment);

        try {
          const response = await systemAPI.updateDepartment(
            currentDepartment.id,
            {
              name: currentDepartment.name,
              code: currentDepartment.code,
              description: currentDepartment.description,
              location: currentDepartment.location,
              managerEmail: currentDepartment.managerEmail,
            }
          );

          console.log("Update response:", response);

          // Update the department in the list
          const updatedDepartments = departments.map((dept) =>
            dept.id === currentDepartment.id ? response.data : dept
          );
          setDepartments(updatedDepartments);

          // Show success message
          setSnackbar({
            open: true,
            message: "Department updated successfully",
            severity: "success",
          });
        } catch (error) {
          console.error("Detailed update error:", error);
          throw error; // Re-throw for the outer catch block
        }
      }

      // Close dialog
      setDialogOpen(false);
    } catch (err) {
      console.error("Error saving department:", err);
      alert("Failed to save department. Please try again.");

      // For development: simulate successful operation
      if (process.env.NODE_ENV === "development") {
        if (dialogMode === "add") {
          const newDepartment = {
            ...currentDepartment,
            id: Date.now().toString(),
          };
          setDepartments([...departments, newDepartment]);
        } else {
          const updatedDepartments = departments.map((dept) =>
            dept.id === currentDepartment.id ? currentDepartment : dept
          );
          setDepartments(updatedDepartments);
        }
        // Close dialog even in development mode
        setDialogOpen(false);
      }
    }
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  // Handle department deletion
  const handleDelete = async (departmentId) => {
    // Ask for confirmation before deleting
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this department?"
    );

    if (confirmDelete) {
      try {
        await systemAPI.deleteDepartment(departmentId);

        // Remove the department from the list
        setDepartments(departments.filter((dept) => dept.id !== departmentId));

        // Show success message
        setSnackbar({
          open: true,
          message: "Department deleted successfully",
          severity: "success",
        });
      } catch (err) {
        console.error("Error deleting department:", err);

        // Show error message
        setSnackbar({
          open: true,
          message: "Failed to delete department. Please try again.",
          severity: "error",
        });

        // For development: simulate successful deletion
        if (process.env.NODE_ENV === "development") {
          setDepartments(
            departments.filter((dept) => dept.id !== departmentId)
          );
        }
      }
    }
  };

  // Search state
  const [search, setSearch] = useState("");

  // Filtered departments
  const filteredDepartments = departments.filter(
    (dept) =>
      dept.name.toLowerCase().includes(search.toLowerCase()) ||
      (dept.code || "").toLowerCase().includes(search.toLowerCase()) ||
      (dept.description || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box>
      {accessDenied ? (
        <Box
          sx={{
            mt: 4,
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            bgcolor: "error.lighter",
            borderRadius: 2,
          }}
        >
          <WarningIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h5" color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography align="center">
            You do not have permission to access the Department Management
            system. This feature is restricted to Developer Master users only.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Redirecting to dashboard...
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              mb: 4,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search departments..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: "100%", sm: 300 } }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog("add")}
              sx={{ minWidth: 160 }}
            >
              Add Department
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Department Overview Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Departments
                  </Typography>
                  <Typography variant="h3">{departments.length}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Administrators
                  </Typography>
                  <Typography variant="h3">
                    {departments.reduce(
                      (sum, dept) => sum + (dept.adminCount || 0),
                      0
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Faculty Members
                  </Typography>
                  <Typography variant="h3">
                    {departments.reduce(
                      (sum, dept) => sum + (dept.facultyCount || 0),
                      0
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Latest Department
                  </Typography>
                  <Typography variant="h5">
                    {departments.length > 0
                      ? departments.sort(
                          (a, b) =>
                            new Date(b.createdAt) - new Date(a.createdAt)
                        )[0].name || "Unnamed Department"
                      : "None"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Department List
            </Typography>
            <Divider />
          </Box>

          {/* Department Table */}
          <TableContainer component={Paper}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Department Name</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell align="center">Admins</TableCell>
                    <TableCell align="center">Faculty</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDepartments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        {departments.length === 0
                          ? "No departments found"
                          : "No results match your search."}
                        {departments.length > 0 && (
                          <Button
                            variant="outlined"
                            onClick={() => setSearch("")}
                            sx={{ mt: 2, ml: 2 }}
                          >
                            Clear Search
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredDepartments.map((department) => (
                      <TableRow key={department.id}>
                        <TableCell component="th" scope="row">
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <BusinessIcon
                              sx={{ mr: 1, color: "primary.main" }}
                            />
                            {department.name}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={department.code} />
                        </TableCell>
                        <TableCell>{department.description}</TableCell>
                        <TableCell>{department.location}</TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={<PeopleIcon />}
                            label={department.adminCount || 0}
                            size="small"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={<PeopleIcon />}
                            label={department.facultyCount || 0}
                            size="small"
                            color="secondary"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenDialog("edit", department)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(department.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>

          {/* Department Form Dialog */}
          <Dialog
            key={dialogMode}
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              {dialogMode === "add" ? "Add New Department" : "Edit Department"}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Department Name"
                    name="name"
                    value={currentDepartment.name}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Department Code"
                    name="code"
                    value={currentDepartment.code}
                    onChange={handleInputChange}
                    required
                    helperText="Short code for the department (e.g., CS, BIO)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={currentDepartment.description}
                    onChange={handleInputChange}
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Location"
                    name="location"
                    value={currentDepartment.location}
                    onChange={handleInputChange}
                    placeholder="Building and floor"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Department Manager Email"
                    name="managerEmail"
                    value={currentDepartment.managerEmail}
                    onChange={handleInputChange}
                    placeholder="department.head@example.com"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!currentDepartment.name || !currentDepartment.code}
              >
                {dialogMode === "add" ? "Create Department" : "Save Changes"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity={snackbar.severity}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </>
      )}
    </Box>
  );
};

export default DepartmentManagement;
